import { useEffect, useMemo, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';

import { ApiError, apiRequest } from '../lib/api-client';

type ReportPeriod = 'TODAY' | 'WEEK' | 'MONTH' | 'CUSTOM';
type PaymentMethod = 'CASH' | 'QRIS' | 'TRANSFER';
type ExpenseCategory =
  | 'FLOWERS'
  | 'WRAPPING'
  | 'RIBBON_ACCESSORIES'
  | 'OPERATIONS'
  | 'SALARY'
  | 'TRANSPORT'
  | 'UTILITIES'
  | 'OTHER';

type FinanceSummary = {
  generatedAt: string;
  timezone: string;
  scope: {
    type: 'OUTLET' | 'CONSOLIDATED';
    outlets: Array<{ id: string; code: string; name: string }>;
  };
  period: { type: ReportPeriod; from: string; to: string; label: string };
  metrics: {
    revenue: number;
    grossProfit: number;
    transactions: number;
    productsSold: number;
    operatingExpenses: number;
    netProfit: number;
  };
  cash: {
    incoming: number;
    outgoing: number;
    netMovement: number;
    currentBalance: number;
    byMethod: Array<{
      method: PaymentMethod;
      balance: number;
      incoming: number;
      outgoing: number;
    }>;
  };
  outstanding: { supplier: number; customer: number };
  history: Array<{
    id: string;
    outletId: string;
    direction: 'IN' | 'OUT';
    entryType: string;
    paymentMethod: PaymentMethod;
    amount: string;
    occurredAt: string;
    sourceType: string;
    sourceId: string;
    description?: string | null;
    reason?: string | null;
    reversalOfId?: string | null;
    outlet?: { code: string; name: string };
  }>;
};

type Payable = {
  id: string;
  number: string;
  totalAmount: string;
  paidAmount: string;
  remainingAmount: string;
  supplier: { id: string; name: string };
};

type Expense = {
  id: string;
  number: string;
  category: ExpenseCategory;
  amount: string;
  paymentMethod: PaymentMethod;
  occurredAt: string;
  notes?: string | null;
  status: 'POSTED' | 'CANCELLED';
  version: number;
};

const currency = new Intl.NumberFormat('id-ID', {
  style: 'currency',
  currency: 'IDR',
  maximumFractionDigits: 0,
});
const periodLabels: Record<ReportPeriod, string> = {
  TODAY: 'Hari Ini',
  WEEK: 'Minggu Ini',
  MONTH: 'Bulan Ini',
  CUSTOM: 'Rentang Tanggal',
};
const paymentLabels: Record<PaymentMethod, string> = {
  CASH: 'Tunai',
  QRIS: 'QRIS',
  TRANSFER: 'Transfer',
};
const expenseLabels: Record<ExpenseCategory, string> = {
  FLOWERS: 'Belanja bunga',
  WRAPPING: 'Belanja wrapping',
  RIBBON_ACCESSORIES: 'Ribbon & aksesoris',
  OPERATIONS: 'Operasional',
  SALARY: 'Gaji',
  TRANSPORT: 'Transportasi',
  UTILITIES: 'Listrik & Internet',
  OTHER: 'Lain-lain',
};
const entryLabels: Record<string, string> = {
  OPENING_BALANCE: 'Saldo Awal',
  READY_STOCK_PAYMENT: 'Penjualan Ready-stock',
  CUSTOM_PAYMENT: 'Pembayaran Custom',
  SUPPLIER_PAYMENT: 'Pembayaran Supplier',
  OPERATING_EXPENSE: 'Biaya Operasional',
  REFUND: 'Refund',
  REVERSAL: 'Reversal',
};

function jakartaToday() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Jakarta',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

function localDateTime() {
  const date = new Date(Date.now() - new Date().getTimezoneOffset() * 60_000);
  return date.toISOString().slice(0, 16);
}

export function Finance({
  outletReady,
  consolidated,
  permissions,
}: {
  outletReady: boolean;
  consolidated: boolean;
  permissions: string[];
}) {
  const currentDate = jakartaToday();
  const [period, setPeriod] = useState<ReportPeriod>('TODAY');
  const [from, setFrom] = useState(currentDate);
  const [to, setTo] = useState(currentDate);
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [payables, setPayables] = useState<Payable[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState<'expense' | 'supplier' | 'opening' | null>(null);

  const query = useMemo(() => {
    const params = new URLSearchParams({ period });
    if (period === 'CUSTOM') {
      params.set('from', from);
      params.set('to', to);
    }
    return params.toString();
  }, [period, from, to]);

  async function load() {
    if (!outletReady || (period === 'CUSTOM' && (!from || !to || from > to))) return;
    setLoading(true);
    setError('');
    try {
      const endpoint = consolidated
        ? `/finance/summary/consolidated?${query}`
        : `/finance/summary?${query}`;
      const next = await apiRequest<FinanceSummary>(endpoint);
      setSummary(next);
      if (!consolidated) {
        const [nextPayables, nextExpenses] = await Promise.all([
          apiRequest<Payable[]>('/finance/payables'),
          apiRequest<Expense[]>('/finance/expenses'),
        ]);
        setPayables(nextPayables);
        setExpenses(nextExpenses);
      } else {
        setPayables([]);
        setExpenses([]);
      }
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Data keuangan gagal dimuat.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [outletReady, consolidated, period]);

  async function mutate(path: string, body: Record<string, unknown>, message: string) {
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await apiRequest(path, { method: 'POST', body: JSON.stringify(body) });
      setSuccess(message);
      setForm(null);
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Transaksi keuangan gagal disimpan.');
    } finally {
      setBusy(false);
    }
  }

  if (!outletReady) {
    return (
      <div className="rounded-2xl bg-amber-50 p-6 text-amber-800">
        Pilih outlet aktif untuk membuka Modul Keuangan.
      </div>
    );
  }

  return (
    <section className="page-shell">
      <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
        <div>
          <p className="section-kicker">Kontrol Kas Outlet</p>
          <h1 className="mt-1 text-3xl font-black tracking-tight text-slate-950">Keuangan</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
            Omzet tetap bersumber dari transaksi selesai. Kas mengikuti waktu pembayaran aktual dan
            koreksi hanya melalui reversal yang dapat ditelusuri.
          </p>
        </div>
        {!consolidated && (
          <div className="flex flex-wrap gap-2">
            {permissions.includes('finance.opening_balance.manage') && (
              <button
                className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-700"
                onClick={() => setForm('opening')}
              >
                Saldo Awal
              </button>
            )}
            {permissions.includes('finance.supplier_payments.create') && (
              <button
                className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-700"
                onClick={() => setForm('supplier')}
              >
                Bayar Supplier
              </button>
            )}
            {permissions.includes('finance.expenses.create') && (
              <button
                className="rounded-xl bg-ajisai-700 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-ajisai-800"
                onClick={() => setForm('expense')}
              >
                + Biaya Operasional
              </button>
            )}
          </div>
        )}
      </div>

      <div className="filter-bar mt-6">
        <div className="flex flex-wrap gap-2">
          {(Object.keys(periodLabels) as ReportPeriod[]).map((item) => (
            <button
              key={item}
              className={`rounded-xl px-4 py-2.5 text-sm font-bold ${period === item ? 'bg-ajisai-700 text-white' : 'border border-slate-300 text-slate-600'}`}
              onClick={() => setPeriod(item)}
            >
              {periodLabels[item]}
            </button>
          ))}
        </div>
        {period === 'CUSTOM' && (
          <form
            className="mt-4 grid gap-3 border-t border-slate-200 pt-4 sm:grid-cols-[1fr_1fr_auto]"
            onSubmit={(event) => {
              event.preventDefault();
              void load();
            }}
          >
            <DateField label="Tanggal Awal" value={from} onChange={setFrom} max={to} />
            <DateField
              label="Tanggal Akhir"
              value={to}
              onChange={setTo}
              min={from}
              max={currentDate}
            />
            <button className="self-end rounded-xl bg-slate-900 px-4 py-2.5 font-bold text-white">
              Terapkan
            </button>
          </form>
        )}
      </div>

      {error && (
        <p role="alert" className="mt-4 rounded-xl bg-red-50 p-4 text-sm text-red-700">
          {error}
        </p>
      )}
      {success && (
        <p className="mt-4 rounded-xl bg-emerald-50 p-4 text-sm text-emerald-700">{success}</p>
      )}
      {loading && (
        <p className="mt-5 animate-pulse text-sm font-bold text-slate-500">Memuat data keuangan…</p>
      )}

      {summary && (
        <>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              label="Saldo Kas Saat Ini"
              value={currency.format(summary.cash.currentBalance)}
              accent="emerald"
            />
            <MetricCard
              label="Kas Masuk Periode"
              value={currency.format(summary.cash.incoming)}
              accent="blue"
            />
            <MetricCard
              label="Kas Keluar Periode"
              value={currency.format(summary.cash.outgoing)}
              accent="rose"
            />
            <MetricCard
              label="Pergerakan Bersih"
              value={currency.format(summary.cash.netMovement)}
              accent="slate"
            />
            <MetricCard
              label="Omzet"
              value={currency.format(summary.metrics.revenue)}
              accent="blue"
            />
            <MetricCard
              label="Laba Kotor"
              value={currency.format(summary.metrics.grossProfit)}
              accent="emerald"
            />
            <MetricCard
              label="Biaya Operasional"
              value={currency.format(summary.metrics.operatingExpenses)}
              accent="rose"
            />
            <MetricCard
              label="Laba Bersih Sederhana"
              value={currency.format(summary.metrics.netProfit)}
              accent="slate"
            />
          </div>

          <div className="mt-6 grid gap-5 lg:grid-cols-3">
            {summary.cash.byMethod.map((item) => (
              <article
                key={item.method}
                className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <p className="text-sm font-bold text-slate-500">{paymentLabels[item.method]}</p>
                <p className="mt-2 text-2xl font-black text-slate-950">
                  {currency.format(item.balance)}
                </p>
                <div className="mt-4 flex justify-between text-xs text-slate-500">
                  <span>Masuk {currency.format(item.incoming)}</span>
                  <span>Keluar {currency.format(item.outgoing)}</span>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-6 grid gap-5 lg:grid-cols-2">
            <MetricCard
              label="Sisa Pembayaran Customer"
              value={currency.format(summary.outstanding.customer)}
              accent="amber"
            />
            <MetricCard
              label="Sisa Pembayaran Supplier"
              value={currency.format(summary.outstanding.supplier)}
              accent="amber"
            />
          </div>

          <article className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 p-5">
              <h2 className="font-black text-slate-950">Histori Kas</h2>
              <p className="mt-1 text-sm text-slate-500">
                Maksimal 100 transaksi pada periode aktif.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-5 py-3">Tanggal</th>
                    <th className="px-5 py-3">Jenis</th>
                    <th className="px-5 py-3">Metode</th>
                    <th className="px-5 py-3">Keterangan</th>
                    <th className="px-5 py-3 text-right">Nominal</th>
                    <th className="px-5 py-3">Outlet</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {summary.history.map((entry) => (
                    <tr key={entry.id}>
                      <td className="whitespace-nowrap px-5 py-4">
                        {new Date(entry.occurredAt).toLocaleString('id-ID')}
                      </td>
                      <td className="px-5 py-4 font-bold">
                        {entryLabels[entry.entryType] ?? entry.entryType}
                      </td>
                      <td className="px-5 py-4">{paymentLabels[entry.paymentMethod]}</td>
                      <td className="max-w-xs px-5 py-4 text-slate-500">
                        {entry.description ?? '—'}
                        {entry.reason ? ` · ${entry.reason}` : ''}
                      </td>
                      <td
                        className={`whitespace-nowrap px-5 py-4 text-right font-black ${entry.direction === 'IN' ? 'text-emerald-700' : 'text-rose-700'}`}
                      >
                        {entry.direction === 'IN' ? '+' : '−'}
                        {currency.format(Number(entry.amount))}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4">{entry.outlet?.code ?? '—'}</td>
                    </tr>
                  ))}
                  {summary.history.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-5 py-10 text-center text-slate-500">
                        Belum ada pergerakan kas pada periode ini.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </article>

          {!consolidated && expenses.length > 0 && (
            <article className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="font-black text-slate-950">Biaya Operasional Terbaru</h2>
              <div className="mt-4 space-y-3">
                {expenses.slice(0, 10).map((expense) => (
                  <div
                    key={expense.id}
                    className="flex flex-col justify-between gap-3 rounded-xl bg-slate-50 p-4 sm:flex-row sm:items-center"
                  >
                    <div>
                      <p className="font-bold">
                        {expense.number} · {expenseLabels[expense.category]}
                      </p>
                      <p className="mt-1 text-sm text-slate-500">
                        {new Date(expense.occurredAt).toLocaleString('id-ID')} ·{' '}
                        {paymentLabels[expense.paymentMethod]}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <strong>{currency.format(Number(expense.amount))}</strong>
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-bold ${expense.status === 'POSTED' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'}`}
                      >
                        {expense.status === 'POSTED' ? 'Tercatat' : 'Dibatalkan'}
                      </span>
                      {expense.status === 'POSTED' &&
                        permissions.includes('finance.expenses.cancel') && (
                          <button
                            className="text-sm font-bold text-rose-700"
                            onClick={() => {
                              const reason = window.prompt('Alasan pembatalan biaya:');
                              if (reason)
                                void mutate(
                                  `/finance/expenses/${expense.id}/cancel`,
                                  {
                                    requestId: crypto.randomUUID(),
                                    version: expense.version,
                                    reason,
                                  },
                                  'Biaya dibatalkan melalui reversal.',
                                );
                            }}
                          >
                            Batalkan
                          </button>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            </article>
          )}
        </>
      )}

      {form === 'expense' && (
        <ExpenseForm
          busy={busy}
          onClose={() => setForm(null)}
          onSubmit={(body) =>
            void mutate('/finance/expenses', body, 'Biaya operasional berhasil dicatat.')
          }
        />
      )}
      {form === 'opening' && (
        <OpeningBalanceForm
          busy={busy}
          onClose={() => setForm(null)}
          onSubmit={(body) =>
            void mutate('/finance/opening-balances', body, 'Saldo awal berhasil dicatat.')
          }
        />
      )}
      {form === 'supplier' && (
        <SupplierPaymentForm
          busy={busy}
          payables={payables}
          onClose={() => setForm(null)}
          onSubmit={(body) =>
            void mutate('/finance/supplier-payments', body, 'Pembayaran supplier berhasil dicatat.')
          }
        />
      )}
    </section>
  );
}

function MetricCard({ label, value, accent }: { label: string; value: string; accent: string }) {
  const colors: Record<string, string> = {
    emerald: 'border-slate-200 border-l-4 border-l-emerald-500 bg-white',
    blue: 'border-slate-200 border-l-4 border-l-ajisai-500 bg-white',
    rose: 'border-slate-200 border-l-4 border-l-rose-500 bg-white',
    amber: 'border-slate-200 border-l-4 border-l-amber-500 bg-white',
    slate: 'border-slate-200 border-l-4 border-l-slate-400 bg-white',
  };
  return (
    <article className={`rounded-2xl border p-5 shadow-card ${colors[accent]}`}>
      <p className="text-sm font-bold text-slate-500">{label}</p>
      <p className="mt-2 text-2xl font-black text-slate-950">{value}</p>
    </article>
  );
}

function DateField({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
}) {
  return (
    <label className="text-sm font-bold text-slate-700">
      {label}
      <input
        className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 font-normal"
        type="date"
        value={value}
        min={min}
        max={max}
        required
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function Modal({
  title,
  busy,
  onClose,
  onSubmit,
  children,
}: {
  title: string;
  busy: boolean;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  children: ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/50 p-4">
      <form
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
        onSubmit={onSubmit}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-black">{title}</h2>
          <button type="button" className="text-2xl text-slate-400" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="mt-5 space-y-4">{children}</div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-xl border border-slate-300 px-4 py-2.5 font-bold text-slate-700"
            onClick={onClose}
          >
            Batal
          </button>
          <button
            disabled={busy}
            className="rounded-xl bg-ajisai-700 px-4 py-2.5 font-bold text-white transition hover:bg-ajisai-800 disabled:opacity-50"
          >
            {busy ? 'Menyimpan…' : 'Simpan'}
          </button>
        </div>
      </form>
    </div>
  );
}

function PaymentSelect({
  value,
  onChange,
}: {
  value: PaymentMethod;
  onChange: (value: PaymentMethod) => void;
}) {
  return (
    <label className="block text-sm font-bold text-slate-700">
      Metode Pembayaran
      <select
        className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 font-normal"
        value={value}
        onChange={(event) => onChange(event.target.value as PaymentMethod)}
      >
        {(Object.keys(paymentLabels) as PaymentMethod[]).map((method) => (
          <option key={method} value={method}>
            {paymentLabels[method]}
          </option>
        ))}
      </select>
    </label>
  );
}

function ExpenseForm({
  busy,
  onClose,
  onSubmit,
}: {
  busy: boolean;
  onClose: () => void;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const [category, setCategory] = useState<ExpenseCategory>('OPERATIONS');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('CASH');
  const [occurredAt, setOccurredAt] = useState(localDateTime());
  const [notes, setNotes] = useState('');
  return (
    <Modal
      title="Tambah Biaya Operasional"
      busy={busy}
      onClose={onClose}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({
          requestId: crypto.randomUUID(),
          category,
          amount,
          paymentMethod: method,
          occurredAt: new Date(occurredAt).toISOString(),
          notes,
        });
      }}
    >
      <label className="block text-sm font-bold">
        Kategori
        <select
          className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 font-normal"
          value={category}
          onChange={(event) => setCategory(event.target.value as ExpenseCategory)}
        >
          {(Object.keys(expenseLabels) as ExpenseCategory[]).map((item) => (
            <option key={item} value={item}>
              {expenseLabels[item]}
            </option>
          ))}
        </select>
      </label>
      <MoneyField value={amount} onChange={setAmount} />
      <PaymentSelect value={method} onChange={setMethod} />
      <DateTimeField value={occurredAt} onChange={setOccurredAt} />
      <NotesField value={notes} onChange={setNotes} />
    </Modal>
  );
}

function OpeningBalanceForm({
  busy,
  onClose,
  onSubmit,
}: {
  busy: boolean;
  onClose: () => void;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('CASH');
  const [occurredAt, setOccurredAt] = useState(localDateTime());
  const [notes, setNotes] = useState('');
  return (
    <Modal
      title="Catat Saldo Awal"
      busy={busy}
      onClose={onClose}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({
          requestId: crypto.randomUUID(),
          amount,
          paymentMethod: method,
          occurredAt: new Date(occurredAt).toISOString(),
          notes,
        });
      }}
    >
      <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800">
        Saldo awal bukan omzet. Koreksi berikutnya harus melalui reversal.
      </p>
      <MoneyField value={amount} onChange={setAmount} />
      <PaymentSelect value={method} onChange={setMethod} />
      <DateTimeField value={occurredAt} onChange={setOccurredAt} />
      <NotesField value={notes} onChange={setNotes} />
    </Modal>
  );
}

function SupplierPaymentForm({
  busy,
  payables,
  onClose,
  onSubmit,
}: {
  busy: boolean;
  payables: Payable[];
  onClose: () => void;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const [receiptId, setReceiptId] = useState(payables[0]?.id ?? '');
  const selected = payables.find((item) => item.id === receiptId);
  const [amount, setAmount] = useState(payables[0]?.remainingAmount ?? '');
  const [method, setMethod] = useState<PaymentMethod>('TRANSFER');
  const [paidAt, setPaidAt] = useState(localDateTime());
  const [notes, setNotes] = useState('');
  return (
    <Modal
      title="Pembayaran Supplier"
      busy={busy}
      onClose={onClose}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({
          requestId: crypto.randomUUID(),
          receiptId,
          amount,
          paymentMethod: method,
          paidAt: new Date(paidAt).toISOString(),
          notes,
        });
      }}
    >
      <label className="block text-sm font-bold">
        Penerimaan
        <select
          required
          className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 font-normal"
          value={receiptId}
          onChange={(event) => {
            setReceiptId(event.target.value);
            setAmount(
              payables.find((item) => item.id === event.target.value)?.remainingAmount ?? '',
            );
          }}
        >
          {payables.map((item) => (
            <option key={item.id} value={item.id}>
              {item.number} · {item.supplier.name} · sisa{' '}
              {currency.format(Number(item.remainingAmount))}
            </option>
          ))}
        </select>
      </label>
      {!payables.length && (
        <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-500">
          Tidak ada penerimaan yang belum lunas.
        </p>
      )}
      {selected && (
        <p className="text-sm text-slate-500">
          Total {currency.format(Number(selected.totalAmount))} · sudah dibayar{' '}
          {currency.format(Number(selected.paidAmount))}
        </p>
      )}
      <MoneyField value={amount} onChange={setAmount} />
      <PaymentSelect value={method} onChange={setMethod} />
      <DateTimeField value={paidAt} onChange={setPaidAt} />
      <NotesField value={notes} onChange={setNotes} />
    </Modal>
  );
}

function MoneyField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <label className="block text-sm font-bold">
      Nominal
      <input
        required
        min="0.01"
        step="0.01"
        type="number"
        className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 font-normal"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}
function DateTimeField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <label className="block text-sm font-bold">
      Tanggal dan Waktu
      <input
        required
        type="datetime-local"
        className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 font-normal"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}
function NotesField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <label className="block text-sm font-bold">
      Catatan
      <textarea
        maxLength={1000}
        rows={3}
        className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 font-normal"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}
